/**
 * richText.js — helpers for the comment rich-text editor.
 *
 * Comments may be authored as formatted HTML (bold, lists, brand-colour text).
 * We sanitise to a tight allowlist before rendering so stored markup can never
 * inject script/handlers. Older comments are plain text and render as-is.
 */
import DOMPurify from 'dompurify';

const URI_RE = /^(?:https?:|mailto:|tel:|#)/i;

// Formatting tags the editor can produce (across browsers: tag-based and
// CSS/span-based execCommand output), plus safe links. DOMPurify still strips
// scripts, event handlers, and dangerous URIs (javascript:, data:, …).
const ALLOWED_TAGS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'p', 'br',
  'ul', 'ol', 'li', 'span', 'div', 'a', 'font', 'blockquote',
];
const ALLOWED_ATTR = ['style', 'color', 'href', 'target', 'rel'];

// Tight allowlist for PASTED content: keep useful structure (bold/italic/lists/
// links) but drop the junk external editors inject — styling spans/divs/fonts,
// inline styles (e.g. white-space:pre-wrap), and background/colour. Users
// re-apply brand colour with the toolbar.
const PASTE_TAGS = ['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'p', 'br', 'ul', 'ol', 'li', 'a'];
const PASTE_ATTR = ['href', 'target', 'rel'];

// Non-breaking spaces (literal entity or the   char) → regular space.
// contentEditable + pasted rich text scatter these; collapse them so text wraps
// and copies normally.
function normalizeNbsp(html) {
  return String(html || '').replace(/&nbsp;/gi, ' ').replace(/ /g, ' ');
}

/**
 * Sanitise comment HTML to the formatting allowlist (no scripts/handlers), and
 * clean legacy paste junk on the way out: normalise &nbsp; and strip every inline
 * style EXCEPT `color` (the intended brand-colour formatting). This also cleans
 * comments already stored with pasted `<span style="white-space:pre-wrap">`/nbsp.
 */
export function sanitizeRich(html) {
  const clean = DOMPurify.sanitize(normalizeNbsp(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ADD_ATTR: ['target'],            // keep target on links
    ALLOWED_URI_REGEXP: URI_RE,
  });
  if (typeof document === 'undefined' || !/style=/.test(clean)) return clean;
  const tmp = document.createElement('div');
  tmp.innerHTML = clean;
  tmp.querySelectorAll('[style]').forEach(el => {
    const color = el.style.color;          // keep only brand colour
    el.removeAttribute('style');
    if (color) el.style.color = color;
  });
  return tmp.innerHTML;
}

/**
 * Clean HTML from the clipboard before inserting into the editor. Strips styling
 * spans/divs/fonts, inline styles, and &nbsp; — leaving bold/italic/lists/links.
 */
export function sanitizePaste(html) {
  return DOMPurify.sanitize(normalizeNbsp(html), {
    ALLOWED_TAGS: PASTE_TAGS,
    ALLOWED_ATTR: PASTE_ATTR,
    ADD_ATTR: ['target'],
    ALLOWED_URI_REGEXP: URI_RE,
  });
}

/** True if the text looks like HTML (so it should render formatted). */
export function isHtml(text) {
  return typeof text === 'string' && /<\/?[a-z][\s\S]*>/i.test(text);
}

/** Plain-text content of a rich value — used for empty checks / previews. */
export function richToText(html) {
  if (!isHtml(html)) return (html || '').trim();
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeRich(html);
  return (tmp.textContent || '').trim();
}
